import { isPlainObject } from 'is-plain-object';

import { Logger } from '@gitbook/util-debug';
import { List, Record } from '@gitbook/util-immutable';

import { isBlock, isBlockRange, MODEL_TYPES } from '../constants';
import type { Block } from './block';
import { Data } from './data';
import type { Document } from './document';
import type { Fragment } from './fragment';
import type { Path } from './lookup-index';
import type { AbstractNode } from './node';
import type { TextRange } from './text-range';

const logger = Logger('slate:block-range');

export interface BlockRangeProperties {
    anchorBlock: string | null;
    focusBlock: string | null;
    isFocused: boolean | null;
    data: Data;
}

export type BlockRangeCreateProps = Partial<BlockRangeProperties>;

/**
 * BlockRange represents a range of blocks in a document. It is used to
 * represent a selection of one or more blocks.
 *
 * It differs from a TextRange in that the selection is of block nodes
 * rather than text characters.
 */
export class BlockRange extends Record<BlockRangeProperties>({
    anchorBlock: null,
    focusBlock: null,
    isFocused: false,
    data: Data.create(),
}) {
    public declare readonly anchorBlock: string | null;
    public declare readonly focusBlock: string | null;
    public declare readonly isFocused: boolean | null;
    public declare readonly data: Data;

    /*
     * Object.
     */

    get object(): 'block-range' {
        return 'block-range';
    }

    /*
     * Check whether the block range is blurred.
     */
    get isBlurred() {
        return !this.isFocused;
    }

    /*
     * Check whether the block range is collapsed to select a single block.
     */
    get isCollapsed() {
        return this.anchorBlock === this.focusBlock;
    }

    /*
     * Check whether the block range is expanded to select more than one block.
     */
    get isExpanded() {
        return !this.isCollapsed;
    }

    /**
     * Check whether the range's keys are set.
     */
    get isSet(): boolean {
        return this.anchorBlock != null && this.focusBlock != null;
    }

    /**
     * Check whether the range's keys are not set.
     */
    get isUnset(): boolean {
        return !this.isSet;
    }

    /**
     * Create a new block range for selection of blocks.
     */
    public static create(attrs: BlockRangeCreateProps = {}) {
        if (BlockRange.isBlockRange(attrs)) {
            return attrs;
        }

        return BlockRange.createRecord({
            ...attrs,
            data: Data.create(attrs.data || {}),
        });
    }

    public static createProperties(
        attrs: BlockRangeCreateProps = {}
    ): Partial<BlockRangeProperties> {
        if (BlockRange.isBlockRange(attrs)) {
            return {
                anchorBlock: attrs.anchorBlock,
                focusBlock: attrs.focusBlock,
                isFocused: attrs.isFocused,
                data: attrs.data,
            };
        }

        if (isPlainObject(attrs)) {
            const props: Partial<BlockRangeProperties> = {};
            if ('anchorBlock' in attrs) {
                props.anchorBlock = attrs.anchorBlock;
            }
            if ('focusBlock' in attrs) {
                props.focusBlock = attrs.focusBlock;
            }
            if ('isFocused' in attrs) {
                props.isFocused = attrs.isFocused;
            }
            if ('data' in attrs) {
                props.data = Data.create(attrs.data);
            }
            return props;
        }

        throw new Error(
            `\`BlockRange.createProperties\` only accepts objects or ranges, but you passed it: ${attrs}`
        );
    }

    /**
     * Create a block range around a single block.
     */
    public static createAtBlock(block: Block): BlockRange {
        return BlockRange.create({
            anchorBlock: block.key,
            focusBlock: block.key,
        });
    }

    public static isBlockRange(maybeBlockRange: unknown): maybeBlockRange is BlockRange {
        return isBlockRange(maybeBlockRange);
    }

    /**
     * Create a BlockRange from a TextRange.
     */
    public static fromTextRange(textRange: TextRange, node: Document): BlockRange {
        const anchorBlock = textRange.anchorKey
            ? node.getAncestors(textRange.anchorKey)?.findLast((v) => v.isBlock())
            : null;
        const focusBlock = textRange.focusKey
            ? node.getAncestors(textRange.focusKey)?.findLast((v) => v.isBlock())
            : null;
        const isFocused = textRange.isFocused;

        if (!anchorBlock || !focusBlock) {
            const block = anchorBlock ?? focusBlock;
            return BlockRange.create({
                anchorBlock: block?.key ?? null,
                focusBlock: block?.key ?? null,
                isFocused,
            });
        }

        return BlockRange.create({
            anchorBlock: anchorBlock?.key ?? null,
            focusBlock: focusBlock?.key ?? null,
            isFocused,
        });
    }

    private findPaths(node: Document, anchorBlockKey: string, focusBlockKey: string): Path[] {
        const anchorPath = node.getPath(anchorBlockKey);
        const focusPath = node.getPath(focusBlockKey);

        if (!anchorPath && !focusPath) {
            return [];
        }

        if (!anchorPath || !focusPath) {
            const path = anchorPath ?? focusPath;
            if (!path) {
                return [];
            }

            return [path, path];
        }

        try {
            // Find the shared path between the anchor and focus
            const commonAncestor = node.getCommonAncestor(anchorBlockKey, focusBlockKey);
            if (!commonAncestor) {
                return [];
            }
            const commonAncestorPath = node.getPath(commonAncestor.key);
            if (commonAncestorPath) {
                return [
                    [...commonAncestorPath, anchorPath[commonAncestorPath.length]],
                    [...commonAncestorPath, focusPath[commonAncestorPath.length]],
                ];
            }
            return [];
        } catch (e) {
            logger.error(e);
            return [];
        }
    }

    public normalize(node: Document): BlockRange {
        let { anchorBlock, focusBlock } = this;

        // 1. validate keys
        if (!anchorBlock && !focusBlock) {
            return this.deselect();
        }

        // There is definitely either an anchorBlock or a focusBlock so we can
        // safely use the focus key as the anchor key if there is no anchor key set
        // (and vice versa). This will create a collapsed block range.
        anchorBlock ??= focusBlock;
        focusBlock ??= anchorBlock;

        // 2. find paths

        const [anchorPath, focusPath] = this.findPaths(node, anchorBlock!, focusBlock!);

        if (!anchorPath && !focusPath) {
            logger.warning(
                'BlockRange is unset because neither anchor or focus paths can be found'
            );
            return this.deselect();
        }
        // 3. find and validate nodes

        let anchorNode = anchorPath ? node.getByPath(anchorPath) : null;
        let focusNode = focusPath ? node.getByPath(focusPath) : null;

        if (!anchorNode && !focusNode) {
            logger.warning(
                'BlockRange is unset because neither anchor or focus nodes can be found'
            );
            return this.deselect();
        }

        if (!anchorNode || !focusNode) {
            anchorNode ??= focusNode;
            focusNode ??= anchorNode;
        }

        // 4. find and validate blocks

        // where the anchor node is a block we can use that node as the anchor
        // otherwise try to find the closest block.
        const anchor =
            anchorNode && !isBlock(anchorNode) ? node.getClosestBlock(anchorNode.key) : anchorNode;

        // logic is same as the anchor node (see comment above), we either pick the node if it is a block
        // or the closest block.
        const focus =
            focusNode && !isBlock(focusNode) ? node.getClosestBlock(focusNode.key) : focusNode;

        // 5. validate the keys one last time

        const normalizedAnchor = anchor?.key ?? null;
        const normalizedFocus = focus?.key ?? null;

        if (!normalizedAnchor && !normalizedFocus) {
            logger.warning(
                'BlockRange is unset because neither anchor or focus blocks can be found'
            );
            return this.deselect();
        }

        return this.merge({
            anchorBlock: (normalizedAnchor ?? normalizedFocus)!,
            focusBlock: (normalizedFocus ?? normalizedAnchor)!,
        });
    }

    /*
     * Normalize the selection to ensure that the anchor and focus are both in blocks.
     */
    public normalizeSelection(document: Document): BlockRange {
        let { anchorBlock, focusBlock } = this;

        if (!anchorBlock && !focusBlock) {
            logger.warning(
                'BlockRange is unset because neither anchorBlock or focusBlock is valid'
            );
            return this.deselect();
        }

        if (!anchorBlock || !focusBlock) {
            anchorBlock ??= focusBlock;
            focusBlock ??= anchorBlock;
        }

        let anchorNode = document.getNode(anchorBlock!);
        let focusNode = document.getNode(focusBlock!);

        if (!anchorNode && !focusNode) {
            logger.warning(
                'BlockRange is unset because neither anchor or focus nodes can be found'
            );
            return this.deselect();
        }

        // If either an anchor node or a focus node exists, but not both,
        // we can collapse the selection to that node.
        if (!anchorNode || !focusNode) {
            anchorNode ??= focusNode;
            focusNode ??= anchorNode;
        }

        let result: BlockRange = this;

        // Make sure the anchorNode is a block and, if not, find the closest block.
        const anchorBlockNode = isBlock(anchorNode)
            ? anchorNode
            : document.getClosestBlock(anchorNode!.key);

        if (anchorBlockNode && anchorBlockNode.key !== anchorNode?.key) {
            result = result.merge({
                anchorBlock: anchorBlockNode.key,
            });
        }

        // Make sure the focusNode is a block and, if not, find the closest block.
        const focusBlockNode = isBlock(focusNode)
            ? focusNode
            : document.getClosestBlock(focusNode!.key);

        if (focusBlockNode && focusBlockNode.key !== focusNode?.key) {
            result = result.merge({
                focusBlock: focusBlockNode.key,
            });
        }

        if (!anchorBlockNode && !focusBlockNode) {
            logger.warning(
                'BlockRange is unset because neither anchor or focus blocks can be found'
            );
            return this.deselect();
        }

        // If the blocks have the same parent or are the same block we are done
        const anchorBlockNodeParent = anchorBlockNode
            ? document.getParent(anchorBlockNode.key)
            : null;
        const focusBlockNodeParent = focusBlockNode ? document.getParent(focusBlockNode.key) : null;
        const isSameNode =
            anchorBlockNode && focusBlockNode && anchorBlockNode.key === focusBlockNode.key;

        if (isSameNode) {
            return result;
        }

        const hasSameParent =
            anchorBlockNodeParent &&
            focusBlockNodeParent &&
            anchorBlockNodeParent.key === focusBlockNodeParent.key;
        if (hasSameParent) {
            return result;
        }

        // If the blocks have different parents, we need to find the common ancestor
        const [anchorPath, focusPath] = this.findPaths(
            document,
            anchorBlockNode!.key,
            focusBlockNode!.key
        );

        let revisedAnchor = document.getByPath(anchorPath);
        if (revisedAnchor && isBlock(revisedAnchor)) {
            revisedAnchor = revisedAnchor.isLeafBlock()
                ? document.getClosestBlock(revisedAnchor.key)
                : revisedAnchor;
            if (revisedAnchor && revisedAnchor.key !== anchorBlockNode?.key) {
                result = result.merge({ anchorBlock: revisedAnchor.key });
            }
        }

        let revisedFocus = document.getByPath(focusPath);
        if (revisedFocus && isBlock(revisedFocus)) {
            revisedFocus = revisedFocus.isLeafBlock()
                ? document.getClosestBlock(revisedFocus.key)
                : revisedFocus;
            if (revisedFocus && revisedFocus.key !== focusBlockNode?.key) {
                result = result.merge({
                    focusBlock: revisedFocus.key,
                });
            }
        }

        return result;
    }

    /*
     * Get the list of blocks in the block range.
     */
    public getBlocks(node: AbstractNode): List<Block> {
        // if there is no context node then return an empty list.
        if (!node) {
            return List.create();
        }
        // if no keys are set then return an empty list.
        if (!this.anchorBlock && !this.focusBlock) {
            return List.create();
        }

        // If there is only one block because either:
        //   1. there is only an anchor block
        //   2. there is only a focus block
        //   3. the anchor and focus blocks are the same
        // Then we can return a list containing just that block or its highest ancestor
        // that is a block and has only one child. This helps with consistency of selection.
        let soleBlock;
        if (!this.focusBlock || this.focusBlock === this.anchorBlock) {
            soleBlock = this.anchorBlock;
        }
        // if there is only a focus block then return a list containing just the focus block.
        if (!this.anchorBlock) {
            soleBlock = this.focusBlock;
        }

        if (soleBlock) {
            const block = node.getNode(soleBlock);
            if (block?.isBlock()) {
                let candidate = node.getParent(block.key);
                let selected = block;
                while (candidate) {
                    if (candidate?.isBlock() && candidate.blockNodes.size === 1) {
                        selected = candidate;
                        candidate = node.getParent(candidate.key);
                    } else {
                        break;
                    }
                }
                return selected?.isBlock() ? List.create([selected]) : List.create();
            }
        }

        const isForward = node.areDescendantsSorted(this.anchorBlock!, this.focusBlock!);
        const startKey = isForward ? this.anchorBlock : this.focusBlock;
        const endKey = isForward ? this.focusBlock : this.anchorBlock;

        const startBlock = startKey ? (node.getNode(startKey) as Block) : null;
        if (!startBlock) {
            return List.create();
        }

        // if the start and end blocks are the first and last sibling of their common ancestor
        // we can return the common ancestor instead of all its children.
        const ancestor = startKey && endKey ? node.getCommonAncestor(startKey, endKey) : null;
        if (ancestor?.isBlock()) {
            const ancestorBlocks = ancestor.blockNodes;
            if (ancestorBlocks.last()?.key === endKey && ancestorBlocks.first()?.key === startKey) {
                return List.create([ancestor]);
            }
        }

        const blocks: Block[] = [];
        blocks.push(startBlock);
        let nextBlock = startKey ? node.getNextSibling(startKey) : null;
        while (nextBlock) {
            if (!nextBlock.isBlock()) {
                continue;
            }
            blocks.push(nextBlock);
            if (nextBlock.key === endKey) {
                break;
            }
            nextBlock = node.getNextSibling(nextBlock.key);
        }

        return List.create(blocks);
    }

    private getIfBlock(node: any): Block | null {
        if (node) {
            if (isBlock(node)) {
                return node;
            }
            const nextBlock = node.getClosestBlock(node.key);
            if (nextBlock) {
                return nextBlock;
            }
        }
        return null;
    }

    /*
     * Collapse the block range and move forward to a new block.
     */
    public moveSelectionForward(node: Document | Fragment | Block): BlockRange {
        if (!this.anchorBlock || !this.focusBlock) {
            logger.warning('Need to normalize the block range before moving selection forward');
            return this;
        }

        const isForward =
            this.anchorBlock === this.focusBlock ||
            node.areDescendantsSorted(this.anchorBlock, this.focusBlock);

        const endKey = isForward ? this.focusBlock : this.anchorBlock;

        const nextBlock = this.findBlockToMoveSelectionTo(endKey, node, (key) =>
            node.getNextSibling(key)
        );

        if (nextBlock) {
            return this.merge({
                anchorBlock: nextBlock.key,
                focusBlock: nextBlock.key,
            });
        }

        return this.merge({
            anchorBlock: endKey,
            focusBlock: endKey,
        });
    }

    /*
     * Collapse the block range and move backward to a new block.
     * If we are at the end of the node, just collapse to the last block.
     */
    public moveSelectionBackward(node: Document | Fragment | Block): BlockRange {
        if (!this.anchorBlock || !this.focusBlock) {
            logger.warning('Need to normalize the block range before moving selection backward');
            return this;
        }

        const isForward =
            this.anchorBlock === this.focusBlock ||
            node.areDescendantsSorted(this.anchorBlock, this.focusBlock);

        const startKey = isForward ? this.anchorBlock : this.focusBlock;

        const nextBlock = this.findBlockToMoveSelectionTo(startKey, node, (key) =>
            node.getPreviousSibling(key)
        );

        if (nextBlock) {
            return this.merge({
                anchorBlock: nextBlock.key,
                focusBlock: nextBlock.key,
            });
        }

        return this.merge({
            anchorBlock: startKey,
            focusBlock: startKey,
        });
    }

    /*
     * Find the next block to move the selection to.
     * Either a sibling block or the sibling of an ancestor block.
     */
    private findBlockToMoveSelectionTo(
        key: string,
        node: Document | Fragment | Block,
        fn: (key: string) => void
    ) {
        const siblingBlock = this.getIfBlock(fn(key));
        if (siblingBlock) {
            return siblingBlock;
        }

        const parent = node.getParent(key);
        if (!parent) {
            return this;
        }

        let parentBlock = this.getIfBlock(node.getParent(key));

        // Handle the case where we are at the top-level of a fragment
        // by selecting the closest block outside the fragment.
        if (!parentBlock && node.isInFragment(key)) {
            const block = node.getClosestBlock(parent.key);
            return block;
        }

        while (parentBlock) {
            const siblingBlock = this.getIfBlock(fn(parentBlock.key));
            if (siblingBlock) {
                return siblingBlock;
            }
            parentBlock = this.getIfBlock(node.getParent(parentBlock.key));
        }
    }

    /**
     * Move the block selection to the first descendant block of the current block or
     * collapse to the current focus block if there are no descendants.
     */
    public moveSelectionToDescendant(node: Document | Fragment | Block): BlockRange {
        if (!this.anchorBlock || !this.focusBlock) {
            logger.warning('Need to normalize the block range before moving selection');
            return this;
        }

        // use the focus block as the starting point for finding the next descendant.
        const currentBlock = node.getNode(this.focusBlock) as Block;
        if (currentBlock) {
            const descendant = currentBlock.findDescendant(
                (descendant) => {
                    if (!isBlock(descendant)) {
                        return false;
                    }
                    const parent = node.getParent(descendant.key);
                    if (!parent) {
                        return false;
                    }
                    return (
                        ['fragment', 'document'].includes(parent.object) ||
                        !descendant.isLeafBlock()
                    );
                },
                { withFragments: true }
            );

            if (descendant) {
                return this.merge({
                    anchorBlock: descendant.key,
                    focusBlock: descendant.key,
                });
            }
        }

        // collapse to the end of the block range if there are no descendants to move to.
        return this.collapseToFocus();
    }

    /**
     * Move the block selection to the closest ancestor block of the current block or
     * collapse to the current focus block if there are no ancestors.
     */
    public moveSelectionToAncestor(node: Document | Fragment | Block): BlockRange {
        if (!this.anchorBlock || !this.focusBlock) {
            logger.warning('Need to normalize the block range before moving selection');
            return this;
        }

        const currentBlock = node.getNode(this.focusBlock) as Block;
        if (currentBlock) {
            const ancestor = node.getClosestBlock(currentBlock.key);
            if (ancestor) {
                return this.merge({
                    anchorBlock: ancestor.key,
                    focusBlock: ancestor.key,
                });
            }
        }

        // collapse to the end of the block range if there are no ancestors to move to.
        return this.collapseToFocus();
    }

    /**
     * Move the focus block selection to the previous sibling or to the parent.
     */
    public moveFocusBackward(node: Document | Fragment | Block): BlockRange {
        if (!this.anchorBlock || !this.focusBlock) {
            logger.warning('Need to normalize the block range before moving selection');
            return this;
        }

        const nextBlock = this.findBlockToMoveSelectionTo(this.focusBlock, node, (key) =>
            node.getPreviousSibling(key)
        );
        if (nextBlock) {
            return this.merge({ focusBlock: nextBlock.key });
        }
        return this;
    }

    /**
     * Move the focus block selection to the next sibling or to the parent.
     */
    public moveFocusForward(node: Document | Fragment | Block): BlockRange {
        if (!this.anchorBlock || !this.focusBlock) {
            logger.warning('Need to normalize the block range before moving selection');
            return this;
        }

        const nextBlock = this.findBlockToMoveSelectionTo(this.focusBlock, node, (key) =>
            node.getNextSibling(key)
        );
        if (nextBlock) {
            return this.merge({ focusBlock: nextBlock.key });
        }

        return this;
    }

    /**
     * Move the focus block selection to a specified block (key).
     */
    public moveFocusTo(key: string): BlockRange {
        return this.merge({ focusBlock: key });
    }

    /**
     * Move the anchor block selection to a specified block (key).
     */
    public moveAnchorTo(key: string): BlockRange {
        return this.merge({ anchorBlock: key });
    }

    /**
     * Grow the block selection to include the next or previous block in the node
     * depending on the direction (order of anchor block and focus block) of the selection.
     */
    public growSelection(node: Document | Fragment | Block): BlockRange {
        if (!this.anchorBlock || !this.focusBlock) {
            logger.warning('Need to normalize the block range before moving selection');
            return this;
        }

        const isForward =
            this.anchorBlock === this.focusBlock ||
            node.areDescendantsSorted(this.anchorBlock, this.focusBlock);

        const nextNode = isForward
            ? node.getNextSibling(this.focusBlock)
            : node.getPreviousSibling(this.focusBlock);
        const nextBlock = this.getIfBlock(nextNode);

        if (nextBlock) {
            return this.merge({
                focusBlock: nextBlock.key,
            });
        }

        return this;
    }

    /**
     * Shrink the block selection to exclude the next or previous block in the node
     * depending on the direction (order of anchor block and focus block) of the selection.
     */
    public shrinkSelection(node: Document | Fragment | Block): BlockRange {
        if (!this.anchorBlock || !this.focusBlock) {
            logger.warning('Need to normalize the block range before shrinking selection');
            return this;
        }

        if (this.isCollapsed) {
            return this;
        }

        const isForward =
            this.anchorBlock === this.focusBlock ||
            node.areDescendantsSorted(this.anchorBlock, this.focusBlock);

        const previousNode = isForward
            ? node.getPreviousSibling(this.focusBlock)
            : node.getNextSibling(this.focusBlock);
        const previousBlock = this.getIfBlock(previousNode);
        if (previousBlock) {
            return this.merge({
                focusBlock: previousBlock.key,
            });
        }

        return this;
    }

    /*
     * Collapse the block range to the anchor block.
     */
    public collapseToAnchor(): BlockRange {
        if (!this.anchorBlock || !this.focusBlock) {
            logger.warning('Need to normalize the block range before collapsing');
            return this;
        }
        return this.merge({
            anchorBlock: this.anchorBlock,
            focusBlock: this.anchorBlock,
        });
    }

    /*
     * Collapse the block range to the focus block.
     */
    public collapseToFocus(): BlockRange {
        if (!this.anchorBlock || !this.focusBlock) {
            logger.warning('Need to normalize the block range before collapsing');
            return this;
        }
        return this.merge({
            anchorBlock: this.focusBlock,
            focusBlock: this.focusBlock,
        });
    }

    public collapseTo(key: string): BlockRange {
        return this.merge({
            anchorBlock: key,
            focusBlock: key,
        });
    }

    public collapseToStart(): BlockRange {
        return this.collapseToAnchor();
    }

    public collapseToEnd(): BlockRange {
        return this.collapseToFocus();
    }

    /*
     * Move the anchor point to the end block of a node.
     */
    public moveAnchorToStartOf(node: Document | Fragment | Block): BlockRange {
        const anchorBlock = node.getBlocks().first()?.key ?? null;
        return this.merge({
            anchorBlock,
        });
    }

    /*
     * Move the anchor point to the last block of a node.
     */
    public moveAnchorToEndOf(node: Document | Fragment | Block): BlockRange {
        const anchorBlock = node.getBlocks().last()?.key ?? null;
        return this.merge({
            anchorBlock,
        });
    }

    /*
     * Move the focus point to the first block of a node.
     */
    public moveFocusToStartOf(node: Document | Fragment | Block): BlockRange {
        const focusBlock = node.getBlocks().first()?.key ?? null;
        return this.merge({
            focusBlock,
        });
    }

    /*
     * Move the focus point to the last block of a node.
     */
    public moveFocusToEndOf(node: Document | Fragment | Block): BlockRange {
        const focusBlock = node.getBlocks().last()?.key ?? null;
        return this.merge({
            focusBlock,
        });
    }

    /*
     * Check whether a node is in the block range selection.
     */
    public hasBlock(node: AbstractNode, doc: Document | Fragment): boolean {
        if (node.object === 'text') {
            return false;
        }

        return node.object === 'block'
            ? !!this.getBlocks(doc).find((block) => {
                  return block.key === node.key;
              })
            : false;
    }

    /*
     * Check whether a node is the anchor block or whether the anchor block is a descendant of the node.
     */
    public hasAnchorIn(node: AbstractNode): boolean {
        if (node.object === 'text') {
            return false;
        }
        return node.object === 'block'
            ? node.key === this.anchorBlock
            : this.anchorBlock != null && node.hasDescendant(this.anchorBlock);
    }

    /*
     * Check whether the anchor block is the first block of a node.
     */
    public hasAnchorAtStartOf(node: AbstractNode): boolean {
        if (node.object === 'text') {
            return false;
        }
        const first = node.nodes.first();
        return first ? first.key === this.anchorBlock : false;
    }

    /*
     *  Check whether the anchor block is the last block of a node.
     */
    public hasAnchorAtEndOf(node: AbstractNode): boolean {
        if (node.object === 'text') {
            return false;
        }
        const last = node.nodes.last();
        return last ? last.key === this.anchorBlock : false;
    }

    /*
     * Check whether a node is the anchor block of this BlockRange.
     */
    public isAnchor(node: AbstractNode): boolean {
        return this.anchorBlock === node.key;
    }

    /*
     * Check whether a node is the focus block or whether the focus block is a descendant of the node.
     */
    public hasFocusIn(node: AbstractNode): boolean {
        if (node.object === 'text') {
            return false;
        }
        return node.object === 'block'
            ? node.key === this.focusBlock
            : this.focusBlock != null && node.hasDescendant(this.focusBlock);
    }

    /*
     * Check whether the focus block is the first block of a node.
     */
    public hasFocusAtStartOf(node: AbstractNode): boolean {
        if (node.object === 'text') {
            return false;
        }
        const first = node.nodes.first();
        return first ? first.key === this.focusBlock : false;
    }

    /*
     * Check whether the focus block is the last block of a node.
     */
    public hasFocusAtEndOf(node: AbstractNode): boolean {
        if (node.object === 'text') {
            return false;
        }
        const last = node.nodes.last();
        return last ? last.key === this.focusBlock : false;
    }

    /*
     * Check whether a node is the focus block of this BlockRange.
     */
    public isFocus(node: AbstractNode): boolean {
        return this.focusBlock === node.key;
    }

    /*
     * Check whether a node is the only selected node in this BlockRange.
     */
    public hasOnlyNodeSelected(node: AbstractNode): boolean {
        return this.isCollapsed && this.isAnchor(node);
    }

    /**
     * Check whether the BlockRange is a single selected node and is focused.
     */
    public hasOnlyNodeFocused(node: AbstractNode): boolean {
        return !!this.isFocused && this.hasOnlyNodeSelected(node);
    }

    /*
     * Focus the block range.
     */
    public focus() {
        return this.merge({
            isFocused: true,
        });
    }

    /*
     * Blur the block range.
     */

    public blur() {
        return this.merge({
            isFocused: false,
        });
    }

    /*
     * Unset the block range
     */
    public deselect(): BlockRange {
        return this.merge({
            anchorBlock: null,
            focusBlock: null,
            isFocused: false,
            data: Data.create(),
        });
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */

BlockRange.prototype[MODEL_TYPES.BLOCK_RANGE] = true;
